/**
 * Compliance issues API — shared by Compliance Dashboard and Review issue mode.
 */
import {
  coerceSectionRef,
  enrichIssueFields,
  normalizeIssueStatus,
} from '../components/compliance-ui/complianceIssuePresenters.js';

const API_BASE = process.env.REACT_APP_COMPLIANCE_API;

function authHeaders(extra = {}) {
  const idToken = typeof localStorage !== 'undefined' ? localStorage.getItem('id_token') : null;
  return {
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    ...extra,
  };
}

async function api(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(body ? { 'Content-Type': 'application/json' } : {}),
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    throw Object.assign(new Error(data?.message || res.statusText), { status: res.status, data });
  }
  return data;
}

function normalizeIssueRow(it, idx, projectId) {
  const rawFinding = it.finding_detail || it.findingDetail;
  const sectionRef =
    coerceSectionRef(it.section_ref || it.sectionRef) ||
    coerceSectionRef(
      typeof rawFinding === 'object' && rawFinding ? rawFinding.section_ref : null
    );

  return enrichIssueFields({
    id: it.issueId || it.issue_id || it.id || `${projectId}:${idx}`,
    issueId: it.issueId || it.issue_id || it.id,
    scan_id: it.scan_id || '',
    display_id: it.display_id || '',
    path_type: it.path_type || '',
    topic: it.topic || '',
    ncCode: it.display_id || it.ncCode || '',
    identifiedIssue: it.finding_description || it.identifiedIssue || '',
    description: it.finding_description || it.description || '',
    expectedCompliance: it.requirement_text || it.expectedCompliance || '',
    categories: it.categories || (it.compliance_type ? [it.compliance_type] : []),
    compliance_type: it.compliance_type,
    complianceType: it.compliance_type || it.complianceType,
    verdict: it.finding_detail?.verdict || null,
    references:
      Array.isArray(it.references) && it.references.length
        ? it.references
        : sectionRef
          ? [sectionRef]
          : [],
    section_ref: sectionRef || '',
    requirement_id: it.requirement_id || '',
    deliverable_doc_ids: it.deliverable_doc_ids || [],
    project_id: it.project_id || projectId,
    assignedTo: it.assigned_to ?? it.assignedTo,
    deadline: it.deadline || '',
    status: normalizeIssueStatus(it.status),
    tags: it.tags || (it.finding_detail?.verdict ? [it.finding_detail.verdict] : []),
    finding_detail: it.finding_detail || it.findingDetail || null,
    sourceDocuments: it.sourceDocuments || it.source_documents,
    source_geometry_ref: it.source_geometry_ref || it.sourceGeometryRef || null,
    createdAt: it.created_at || it.createdAt || '',
  });
}

/**
 * @param {string} projectId
 * @param {{ limit?: number, lastKey?: string, signal?: AbortSignal }} [opts]
 */
export async function fetchProjectComplianceIssues(projectId, opts = {}) {
  if (!projectId) return { items: [], nextKey: null, totalCount: 0 };
  const limit = opts.limit || 100;
  const lastKey = opts.lastKey ? `&lastKey=${encodeURIComponent(opts.lastKey)}` : '';
  const data = await api(`/projects/${projectId}/issues?limit=${limit}${lastKey}`, {
    signal: opts.signal,
  });
  const items = (data.items || []).map((it, idx) => normalizeIssueRow(it, idx, projectId));
  return {
    items,
    nextKey: data.nextKey || null,
    totalCount: data.totalCount || items.length,
  };
}
